import { createApi, type BaseQueryFn } from "@reduxjs/toolkit/query/react";
import { FeathersError } from "@spork/feathers-client";
import type {
  Child,
  LeaveRequest,
  LeaveRequestAttachment,
  LeaveRequestAttachmentData,
  LeaveRequestData,
  MedicalRequest,
  MedicalRequestData,
  ParentSession,
  PickupRequest,
  PickupRequestData,
  Profile,
  ProfilePatch,
  SchoolYear,
} from "@spork/s4p-server-sdk";
import { Platform } from "react-native";

import { log } from "@/logger";

import {
  query,
  type ResubmitLeaveRequestArgs,
  type UpdateLeaveRequestEndDateArgs,
  type UpdateMedicalRequestArgs,
} from "./queries";

type LoadDataArgs = {
  fn?: (data?: unknown) => Promise<unknown>;
  data?: unknown;
};

const loadData: BaseQueryFn<LoadDataArgs> = async (args) => {
  log.debug("loadData", args);
  try {
    const data = args.fn ? await args.fn(args.data) : null;
    return { data };
  } catch (error) {
    if (error instanceof FeathersError) {
      return { error: error.toJSON() };
    }

    const err = error as Error;
    return { error: err.toString() };
  }
};

export const s4pApi = createApi({
  reducerPath: "s4pApi",
  baseQuery: loadData,
  // Native only — web window focus would refetch on every tab/app switch.
  refetchOnFocus: Platform.OS !== "web",
  refetchOnReconnect: true,
  refetchOnMountOrArgChange: true,
  tagTypes: [
    "Children",
    "LeaveRequests",
    "MedicalRequests",
    "PickupRequests",
    "Profiles",
    "Profile",
    "ParentSessions",
    "SchoolYear",
  ],
  endpoints: (builder) => ({
    getChildren: builder.query<Child[], void>({
      query: () => ({ fn: query.getChildren }),
      providesTags: ["Children"],
    }),
    getLeaveRequests: builder.query<LeaveRequest[], number>({
      query: (childId) => ({ fn: query.getLeaveRequests, data: childId }),
      providesTags: ["LeaveRequests"],
    }),
    getLeaveRequestsForChildren: builder.query<LeaveRequest[], number[]>({
      query: (childIds) => ({
        fn: query.getLeaveRequestsForChildren,
        data: [...childIds].sort((left, right) => left - right),
      }),
      providesTags: ["LeaveRequests"],
    }),
    getLeaveRequest: builder.query<LeaveRequest, number>({
      query: (id) => ({
        fn: async () => query.getLeaveRequest(id),
      }),
      providesTags: ["LeaveRequests"],
    }),
    createLeaveRequest: builder.mutation<LeaveRequest[], LeaveRequestData>({
      query: (data) => ({ fn: query.createLeaveRequest, data }),
      invalidatesTags: ["LeaveRequests"],
    }),
    cancelLeaveRequest: builder.mutation<LeaveRequest, number>({
      query: (id) => ({
        fn: async () => query.cancelLeaveRequest(id),
      }),
      invalidatesTags: ["LeaveRequests"],
    }),
    resubmitLeaveRequest: builder.mutation<LeaveRequest, ResubmitLeaveRequestArgs>({
      query: (args) => ({
        fn: async () => query.resubmitLeaveRequest(args),
      }),
      invalidatesTags: ["LeaveRequests"],
    }),
    updateLeaveRequestEndDate: builder.mutation<
      LeaveRequest,
      UpdateLeaveRequestEndDateArgs
    >({
      query: (args) => ({
        fn: async () => query.updateLeaveRequestEndDate(args),
      }),
      invalidatesTags: ["LeaveRequests"],
    }),
    createLeaveRequestAttachment: builder.mutation<
      LeaveRequestAttachment,
      LeaveRequestAttachmentData
    >({
      query: (data) => ({
        fn: async () => query.createLeaveRequestAttachment(data),
      }),
      invalidatesTags: ["LeaveRequests"],
    }),
    removeLeaveRequestAttachment: builder.mutation<LeaveRequestAttachment, number>({
      query: (id) => ({
        fn: async () => query.removeLeaveRequestAttachment(id),
      }),
      invalidatesTags: ["LeaveRequests"],
    }),

    getMedicalRequestsForChildren: builder.query<MedicalRequest[], number[]>({
      query: (childIds) => ({
        fn: query.getMedicalRequestsForChildren,
        data: [...childIds].sort((left, right) => left - right),
      }),
      providesTags: ["MedicalRequests"],
    }),
    getMedicalRequest: builder.query<MedicalRequest, number>({
      query: (id) => ({
        fn: async () => query.getMedicalRequest(id),
      }),
      providesTags: ["MedicalRequests"],
    }),
    createMedicalRequest: builder.mutation<MedicalRequest, MedicalRequestData>({
      query: (data) => ({ fn: query.createMedicalRequest, data }),
      invalidatesTags: ["MedicalRequests"],
    }),
    updateMedicalRequest: builder.mutation<MedicalRequest, UpdateMedicalRequestArgs>({
      query: (args) => ({
        fn: async () => query.updateMedicalRequest(args),
      }),
      invalidatesTags: ["MedicalRequests"],
    }),
    revokeMedicalRequest: builder.mutation<MedicalRequest, number>({
      query: (id) => ({
        fn: async () => query.revokeMedicalRequest(id),
      }),
      invalidatesTags: ["MedicalRequests"],
    }),

    getPickupRequestsForChildren: builder.query<PickupRequest[], number[]>({
      query: (childIds) => ({
        fn: query.getPickupRequestsForChildren,
        data: [...childIds].sort((left, right) => left - right),
      }),
      providesTags: ["PickupRequests"],
    }),
    createPickupRequest: builder.mutation<PickupRequest[], PickupRequestData>({
      query: (data) => ({ fn: query.createPickupRequest, data }),
      invalidatesTags: ["PickupRequests"],
    }),
    revokePickupRequest: builder.mutation<PickupRequest, number>({
      query: (id) => ({
        fn: async () => query.revokePickupRequest(id),
      }),
      invalidatesTags: ["PickupRequests"],
    }),

    getSchoolYear: builder.query<SchoolYear, number>({
      query: (userId) => ({
        fn: async () => query.getSchoolYear(userId),
      }),
      providesTags: (_result, _error, userId) => [{ type: "SchoolYear", id: userId }],
    }),

    getProfiles: builder.query<Profile[], void>({
      query: () => ({ fn: query.getProfiles }),
      providesTags: ["Profiles"],
    }),

    getProfile: builder.query<Profile, number>({
      query: (id) => ({
        fn: async () => query.getProfile(id),
      }),
      providesTags: (_result, _error, id) => [{ type: "Profile", id }],
    }),

    patchProfile: builder.mutation<Profile, { id: number; data: ProfilePatch }>({
      query: ({ id, data }) => ({
        fn: async () => query.patchProfile({ id, data }),
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Profile", id },
        "Profiles",
        "Children",
      ],
    }),

    getParentSessions: builder.query<ParentSession[], void>({
      query: () => ({ fn: query.getParentSessions }),
      providesTags: ["ParentSessions"],
    }),

    revokeParentSession: builder.mutation<ParentSession, number>({
      query: (id) => ({
        fn: async () => query.revokeParentSession(id),
      }),
      invalidatesTags: ["ParentSessions"],
    }),

    revokeAllParentSessions: builder.mutation<ParentSession[], void>({
      query: () => ({
        fn: async () => query.revokeAllParentSessions(),
      }),
      invalidatesTags: ["ParentSessions"],
    }),
  }),
});

export const {
  useGetChildrenQuery,
  useGetLeaveRequestsQuery,
  useGetLeaveRequestsForChildrenQuery,
  useGetLeaveRequestQuery,
  useCreateLeaveRequestMutation,
  useCancelLeaveRequestMutation,
  useResubmitLeaveRequestMutation,
  useUpdateLeaveRequestEndDateMutation,
  useCreateLeaveRequestAttachmentMutation,
  useRemoveLeaveRequestAttachmentMutation,
  useGetMedicalRequestsForChildrenQuery,
  useGetMedicalRequestQuery,
  useCreateMedicalRequestMutation,
  useUpdateMedicalRequestMutation,
  useRevokeMedicalRequestMutation,
  useGetPickupRequestsForChildrenQuery,
  useCreatePickupRequestMutation,
  useRevokePickupRequestMutation,
  useGetSchoolYearQuery,
  useGetProfilesQuery,
  useGetProfileQuery,
  usePatchProfileMutation,
  useGetParentSessionsQuery,
  useRevokeParentSessionMutation,
  useRevokeAllParentSessionsMutation,
} = s4pApi;
